import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { CellWrap } from "@/components/board/cells/CellWrap";
import { MarkdownInline, MarkdownText } from "@/components/ui/MarkdownText";
import { Popover } from "@/components/ui/Popover";
import { t } from "@/lib/strings";
import type { MentionItem } from "@/lib/types";

const FONT = "Inter Variable, Inter, system-ui, sans-serif";

// Heavy (TipTap / react-markdown). Lazy so they never enter the eager table or
// detail bundle — they load only when a user actually opens a long-text field.
const LongTextRichEditor = lazy(() =>
  import("@/components/board/cells/LongTextRichEditor").then((m) => ({
    default: m.LongTextRichEditor,
  })),
);
const MarkdownContent = lazy(() =>
  import("@/components/ui/MarkdownContent").then((m) => ({ default: m.MarkdownContent })),
);

// Rich display of stored markdown. The lightweight MarkdownText shows instantly
// as the Suspense fallback while react-markdown loads, then upgrades — so
// headings / code blocks / inline images / @-mentions all render.
function RichDisplay({ value }: { value: string }) {
  return (
    <Suspense fallback={<MarkdownText text={value} />}>
      <MarkdownContent markdown={value} />
    </Suspense>
  );
}

function EditorSkeleton({ minHeight = 90 }: { minHeight?: number }) {
  return (
    <div
      style={{
        minHeight,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        background: "var(--surface)",
      }}
    />
  );
}

interface LongTextCellProps {
  value: string;
  rowH?: number;
  /** Detail view: render the full rich editor inline instead of the one-line
   * table cell + popover, so long content isn't clipped. */
  expanded?: boolean;
  onPatch?: (value: string) => void;
  /** Detail view, but the viewer may not edit this field (e.g. a ticket's
   * requester Beschreibung shown to staff). Renders the full rich markdown
   * read-only rather than the clipped one-line table view. */
  readOnly?: boolean;
  /** @-mention candidates (board people). Available in the detail/sheet; the
   * table popover passes none. */
  mentionItems?: MentionItem[];
}

const MOBILE_MQ = "(max-width: 640px)";

export function LongTextCell({
  value,
  rowH = 44,
  expanded = false,
  onPatch,
  readOnly = false,
  mentionItems,
}: LongTextCellProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [isMobile, setIsMobile] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const commitOnCloseRef = useRef(true);

  // Locked detail field: full rich display, no editor affordance at all.
  if (expanded && readOnly) {
    return <RichDisplay value={value} />;
  }

  if (expanded && onPatch) {
    return <InlineLongText value={value} onPatch={onPatch} mentionItems={mentionItems} />;
  }

  if (!onPatch) {
    return (
      <CellWrap rowH={rowH} interactive={false}>
        <span
          title={value}
          style={{
            fontSize: 13,
            color: "var(--ink-2)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
            fontFamily: FONT,
          }}
        >
          {value ? (
            <MarkdownInline text={value} />
          ) : (
            <span style={{ color: "var(--ink-4)" }}>{t.addNotes}</span>
          )}
        </span>
      </CellWrap>
    );
  }

  const openEditor = () => {
    setDraft(value);
    commitOnCloseRef.current = true;
    setIsMobile(window.matchMedia(MOBILE_MQ).matches);
    setOpen(true);
  };

  const closeWithCommit = () => {
    if (commitOnCloseRef.current && draft !== value) {
      onPatch(draft);
    }
    setOpen(false);
  };

  const commitNow = () => {
    if (draft !== value) onPatch(draft);
    commitOnCloseRef.current = false;
    setOpen(false);
  };

  const revertAndClose = () => {
    commitOnCloseRef.current = false;
    setOpen(false);
  };

  return (
    <>
      <CellWrap rowH={rowH} onClick={openEditor}>
        <div ref={anchorRef} style={{ width: "100%", display: "flex", alignItems: "center" }}>
          <span
            title={value}
            style={{
              fontSize: 13,
              color: "var(--ink-2)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
              fontFamily: FONT,
            }}
          >
            {value ? (
              <MarkdownInline text={value} />
            ) : (
              <span style={{ color: "var(--ink-4)" }}>{t.addNotes}</span>
            )}
          </span>
        </div>
      </CellWrap>
      {open ? (
        isMobile ? (
          <MobileSheet onClose={closeWithCommit}>
            <LongTextEditor
              draft={draft}
              setDraft={setDraft}
              onCommit={commitNow}
              onRevert={revertAndClose}
              showSaveButton
            />
          </MobileSheet>
        ) : (
          <Popover anchor={anchorRef.current} onClose={closeWithCommit} width={360}>
            <LongTextEditor
              draft={draft}
              setDraft={setDraft}
              onCommit={commitNow}
              onRevert={revertAndClose}
            />
          </Popover>
        )
      ) : null}
    </>
  );
}

// Detail-view long text: renders the stored markdown (rich) and, on click, drops
// into the WYSIWYG editor inline. Commit on blur (Cmd/Ctrl+Enter blurs to commit;
// Escape reverts). No truncation.
function InlineLongText({
  value,
  onPatch,
  mentionItems,
}: {
  value: string;
  onPatch: (v: string) => void;
  mentionItems?: MentionItem[];
}) {
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const [draft, setDraft] = useState(value);
  const dirtyRef = useRef(false);
  const revertRef = useRef(false);

  // Re-seed from the cell value only when NOT editing, so a WS echo can't clobber
  // an in-progress edit.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Commit with the editor's own final markdown (not the possibly-stale draft).
  // dirtyRef gates: a blur with no edits must not save the round-tripped markdown.
  const commit = (md: string) => {
    if (!revertRef.current && dirtyRef.current && md !== value) onPatch(md);
    revertRef.current = false;
    dirtyRef.current = false;
    setEditing(false);
  };

  if (!editing) {
    return (
      <div
        onClick={() => {
          setDraft(value);
          dirtyRef.current = false;
          revertRef.current = false;
          setEditing(true);
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          minHeight: 40,
          padding: "8px 10px",
          fontSize: 13.5,
          lineHeight: 1.5,
          color: "var(--ink-1)",
          background: "var(--surface)",
          border: `1px solid ${hover ? "var(--hairline)" : "transparent"}`,
          borderRadius: 8,
          cursor: "text",
          fontFamily: FONT,
        }}
      >
        {value ? (
          <RichDisplay value={value} />
        ) : (
          <span style={{ color: "var(--ink-4)" }}>{t.addNotes}</span>
        )}
      </div>
    );
  }

  return (
    <Suspense fallback={<EditorSkeleton />}>
      <LongTextRichEditor
        value={draft}
        onChange={(md) => {
          dirtyRef.current = true;
          setDraft(md);
        }}
        onCommit={commit}
        onEscape={() => {
          revertRef.current = true;
          setEditing(false);
        }}
        mentionItems={mentionItems}
        placeholder={t.addNotes}
      />
    </Suspense>
  );
}

interface LongTextEditorProps {
  draft: string;
  setDraft: (v: string) => void;
  onCommit: () => void;
  onRevert: () => void;
  mentionItems?: MentionItem[];
  showSaveButton?: boolean;
}

function LongTextEditor({
  draft,
  setDraft,
  onCommit,
  onRevert,
  mentionItems,
  showSaveButton = false,
}: LongTextEditorProps) {
  return (
    <div style={{ padding: 10 }}>
      <Suspense fallback={<EditorSkeleton minHeight={120} />}>
        <LongTextRichEditor
          value={draft}
          onChange={setDraft}
          onSubmit={onCommit}
          onEscape={onRevert}
          mentionItems={mentionItems}
          placeholder={t.addNotes}
        />
      </Suspense>
      {showSaveButton ? (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <button
            onClick={onCommit}
            style={{
              height: 36,
              padding: "0 16px",
              fontSize: 13.5,
              fontWeight: 500,
              fontFamily: FONT,
              color: "#fff",
              background: "var(--brand-blue, #0060A8)",
              border: "1px solid transparent",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            {t.save}
          </button>
        </div>
      ) : null}
    </div>
  );
}

interface MobileSheetProps {
  onClose: () => void;
  children: React.ReactNode;
}

function MobileSheet({ onClose, children }: MobileSheetProps) {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target as Node)) onClose();
    };
    const id = window.setTimeout(() => {
      document.addEventListener("mousedown", onDown);
      document.addEventListener("touchstart", onDown);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [onClose]);
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 400,
        background: "rgba(13,62,99,0.18)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <div
        ref={sheetRef}
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          background: "var(--surface)",
          color: "var(--ink-1)",
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          border: "1px solid var(--hairline-strong)",
          borderBottom: "none",
          boxShadow: "0 -10px 28px rgba(13,62,99,0.18)",
          fontFamily: FONT,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        {children}
      </div>
    </div>
  );
}
